import { createContext, useContext, useState, useCallback } from "react";
import { assessments as initialAssessments } from "../data/assessments";
import { toast } from "../hooks/use-toast";

const AssessmentContext = createContext(null);

export const useAssessments = () => {
  const ctx = useContext(AssessmentContext);
  if (!ctx)
    throw new Error("useAssessments must be used within AssessmentProvider");
  return ctx;
};

export const AssessmentProvider = ({ children }) => {
  const [assessments, setAssessments] = useState(initialAssessments);

  const createAssessment = useCallback((data) => {
    const id =
      data.companyName.toLowerCase().replace(/\s+/g, "-") + "-" + Date.now();
    const newAssessment = {
      id,
      company: data.companyName,
      contact: data.keyContact,
      contactTitle: data.contactTitle || "",
      email: data.email,
      phone: data.phone,
      progress: 0,
      type: "AI Readiness",
      status: "active",
      interviews: { total: 0, completed: 0 },
      attention: "No interviews started",
      interviewees: [],
    };
    setAssessments((prev) => [newAssessment, ...prev]);
    toast({
      title: "Assessment created",
      description: `${data.companyName} has been added.`,
    });
  }, []);

  const addInterviewees = useCallback((assessmentId, newInterviewees) => {
    setAssessments((prev) =>
      prev.map((a) => {
        if (a.id !== assessmentId) return a;
        const fullInterviewees = newInterviewees.map((i) => ({
          ...i,
          interview: "pending",
          transcript: null,
          actions: ["record", "upload", "schedule"],
        }));
        const updated = {
          ...a,
          interviewees: [...a.interviewees, ...fullInterviewees],
          interviews: {
            total: a.interviewees.length + fullInterviewees.length,
            completed: a.interviews.completed,
          },
        };
        return updated;
      }),
    );
    toast({
      title: "Interviewees added",
      description: `${newInterviewees.length} interviewee(s) added successfully.`,
    });
  }, []);

  const deleteAssessment = useCallback((assessmentId) => {
    setAssessments((prev) => prev.filter((a) => a.id !== assessmentId));
    toast({
      title: "Assessment deleted",
      description: "The assessment has been removed.",
    });
  }, []);

  const deleteInterviewee = useCallback((assessmentId, intervieweeName) => {
    setAssessments((prev) =>
      prev.map((a) => {
        if (a.id !== assessmentId) return a;
        const filtered = a.interviewees.filter(
          (i) => i.name !== intervieweeName,
        );
        return {
          ...a,
          interviewees: filtered,
          interviews: {
            total: filtered.length,
            completed: filtered.filter((i) => i.interview === "completed")
              .length,
          },
        };
      }),
    );
    toast({
      title: "Interviewee removed",
      description: `${intervieweeName} has been removed.`,
    });
  }, []);

  return (
    <AssessmentContext.Provider
      value={{
        assessments,
        createAssessment,
        addInterviewees,
        deleteAssessment,
        deleteInterviewee,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};
